import { Box, GridLegacy as Grid, Skeleton, Typography, useTheme } from "@mui/material";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";

import {
  ChemicalFacilitiesSummaryQuery,
  TierIiReportEncampStatus,
} from "../../../../generated-graphql/graphql";
import { AssignmentManagementTableFilterKeys } from "../AssignmentManagerTable/useAssignmentManagerOmnisearch";
import { KeyMetric } from "../WorkflowOverviewStats/KeyMetric";

function daysUntilMarchFirst(): number {
  const now = new Date();
  let nextMarch = new Date(now.getFullYear(), 2, 1);

  // If the current date is after March 1, calculate days until March 1 next year
  if (now > nextMarch) {
    nextMarch = new Date(now.getFullYear() + 1, 2, 1);
  }

  const oneDay = 1000 * 60 * 60 * 24; // milliseconds in a day
  const daysLeft = Math.ceil((nextMarch.getTime() - now.getTime()) / oneDay);

  return daysLeft;
}

/**
 * Metric IDs that map to filter actions
 */
export type KeyMetricId =
  | "daysRemaining"
  | "reportsRemaining"
  | "reportingFacilities"
  | "unverifiedReports"
  | "processingVerified"
  | "filingComplete";

type Props = {
  data?: ChemicalFacilitiesSummaryQuery["reportingSeasonSummary"];
  loading: boolean;
};

export function KeyMetrics({
  data = {
    noReportsTotal: 0,
    organizationSummaries: [],
    year: currentTierIIReportingYear,
    facilitiesTotal: 0,
    reportsInFilingComplete: 0,
    reportsInAwaitingVerification: 0,
    reportsInNotReporting: 0,
    reportsInProcessing: 0,
    reportsUnverified: 0,
    reportsTotal: 0,
    workflowsCountHuman: 0,
    workflowsCountError: 0,
    workflowsCountWaitForAll: 0,
    stateSummaries: [],
  },
  loading,
}: Props) {
  const theme = useTheme();
  const { createUrlFromOmnisearchKeyValues } = useOmnisearchDatagrid({
    persistenceKey: "key-metrics",
  });
  const reportsRemaining = data.reportsTotal - data.reportsInFilingComplete;

  const keyMetrics = useMemo(
    () => [
      {
        id: "daysRemaining" as KeyMetricId,
        title: "Days Remaining",
        number: data.year === currentTierIIReportingYear ? daysUntilMarchFirst() : "N/A",
        filter: undefined,
      },
      {
        id: "reportsRemaining" as KeyMetricId,
        title: "Reports Remaining",
        number: reportsRemaining,
        filter: ["reportsRemaining", "true"] as [string, string],
      },
      {
        id: "reportingFacilities" as KeyMetricId,
        title: "Reporting Facilities",
        number: data.reportsTotal,
        filter: ["reportingFacilities", "true"] as [string, string],
      },
      {
        id: "unverifiedReports" as KeyMetricId,
        title: "Unverified Reports",
        number: data.reportsUnverified,
        filter: [AssignmentManagementTableFilterKeys.Unverified, "true"] as [string, string],
      },
      {
        id: "processingVerified" as KeyMetricId,
        title: "Processing (Verified)",
        number: data.reportsInProcessing,
        filter: [
          AssignmentManagementTableFilterKeys.EncampStatus,
          TierIiReportEncampStatus.Processing,
        ] as [string, string],
      },
      {
        id: "filingComplete" as KeyMetricId,
        title: "Filing Complete 🎉",
        number: data.reportsInFilingComplete,
        filter: [
          AssignmentManagementTableFilterKeys.EncampStatus,
          TierIiReportEncampStatus.FilingComplete,
        ] as [string, string],
      },
    ],
    [
      data.year,
      reportsRemaining,
      data.reportsTotal,
      data.reportsUnverified,
      data.reportsInProcessing,
      data.reportsInFilingComplete,
    ]
  );

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          p: 3,
          height: "100%",
          animation: "fadeIn 0.3s ease-in-out",
          "@keyframes fadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
          Key Metrics
        </Typography>

        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item key={i} xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={60}
                sx={{
                  borderRadius: 1,
                  bgcolor: theme.palette.grey[200],
                  animation: `slideIn 0.3s ease-out ${i * 0.1}s both`,
                  "@keyframes slideIn": {
                    from: {
                      opacity: 0,
                      transform: "translateY(10px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        p: 3,
        height: "100%",
        animation: "fadeIn 0.5s ease-in-out",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
        Key Metrics
      </Typography>

      <Grid container spacing={2}>
        {keyMetrics.map((metric, i) => (
          <Grid item key={i} xs={12} md={6}>
            <KeyMetric
              name={metric.title}
              value={metric.number}
              loading={loading}
              href={
                metric.filter
                  ? createUrlFromOmnisearchKeyValues({
                      keyValues: [metric.filter],
                      keysToKeep: ["reportingYear", "reportKind"],
                    })
                  : undefined
              }
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
